// MARK://dhk8/pos/features/tour/GuidedTour
// Coachmarks overlay: a dimmed scrim with a "hole" punched over the current
// step's target element, plus a tooltip card (text + Anterior/Siguiente/Saltar).
// Triggered by `?tour=1` (the onboarding wizard redirects here with it) — see
// useGuidedTour. On finish/skip it clears `?tour=1` and persists "tour seen".
//
// Targeting: each step names a CSS selector. If the element isn't on screen
// (e.g. the nav rail is collapsed into the drawer at ≤520px), the step degrades
// gracefully — no spotlight, the card centers. So it never breaks the 375px
// phone layout. Themed via --pos-* tokens, responsive.

import { useEffect, useLayoutEffect, useState } from 'react'

import { t, type I18nKey } from '../../i18n/es-MX.js'
import { useGuidedTour } from './useGuidedTour.js'

export interface GuidedTourStep {
  /** CSS selector for the element to spotlight (first match wins). */
  target: string
  /** i18n key for the explanatory copy. */
  bodyKey: I18nKey
  /** Optional short title (i18n key). */
  titleKey?: I18nKey
}

interface Rect { top: number; left: number; width: number; height: number }

function measure(selector: string): Rect | null {
  if (typeof document === 'undefined') return null
  let el: Element | null = null
  try {
    el = document.querySelector(selector)
  } catch {
    return null
  }
  if (!el) return null
  const r = el.getBoundingClientRect()
  if (r.width === 0 && r.height === 0) return null
  return { top: r.top, left: r.left, width: r.width, height: r.height }
}

const vw = () => (typeof window !== 'undefined' ? window.innerWidth : 360)
const vh = () => (typeof window !== 'undefined' ? window.innerHeight : 640)

export default function GuidedTour({ steps, force = false }: { steps: readonly GuidedTourStep[]; force?: boolean }) {
  const tour = useGuidedTour(steps.length, force)
  const step = steps[tour.index]
  const [rect, setRect] = useState<Rect | null>(null)

  // Re-measure the target on step change, resize and scroll.
  useLayoutEffect(() => {
    if (!tour.active || !step) return
    const update = () => setRect(measure(step.target))
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [tour.active, step])

  // Esc skips the tour.
  useEffect(() => {
    if (!tour.active) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') tour.skip() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [tour.active, tour])

  if (!tour.active || !step) return null

  const pad = 6
  const hole = rect
    ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }
    : null

  // Card placement: below the hole if there's room, else above; clamped to the
  // viewport. No hole ⇒ dead-center.
  const cardW = Math.min(340, vw() - 24)
  let cardTop: number
  let cardLeft: number
  if (hole) {
    const below = hole.top + hole.height + 12
    const wantBelow = below + 170 < vh()
    cardTop = wantBelow ? below : Math.max(12, hole.top - 12 - 170)
    cardLeft = Math.min(Math.max(12, hole.left + hole.width / 2 - cardW / 2), vw() - cardW - 12)
  } else {
    cardTop = vh() / 2 - 90
    cardLeft = vw() / 2 - cardW / 2
  }

  const isFirst = tour.index === 0
  const isLast = tour.index === tour.count - 1

  return (
    <div className="pos-tour" role="dialog" aria-modal="true" aria-label="Guía rápida">
      {hole ? (
        <>
          <div className="pos-tour__scrim" style={{ top: 0, left: 0, right: 0, height: Math.max(0, hole.top) }} />
          <div className="pos-tour__scrim" style={{ top: hole.top, left: 0, width: Math.max(0, hole.left), height: hole.height }} />
          <div className="pos-tour__scrim" style={{ top: hole.top, left: hole.left + hole.width, right: 0, height: hole.height }} />
          <div className="pos-tour__scrim" style={{ top: hole.top + hole.height, left: 0, right: 0, bottom: 0 }} />
          <div className="pos-tour__ring" style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }} aria-hidden />
        </>
      ) : (
        <div className="pos-tour__scrim" style={{ inset: 0 }} />
      )}

      <div className="pos-tour__card" style={{ top: cardTop, left: cardLeft, width: cardW }}>
        <div className="pos-tour__step">{tour.index + 1} / {tour.count}</div>
        {step.titleKey && <div className="pos-tour__title">{t(step.titleKey)}</div>}
        <p className="pos-tour__body">{t(step.bodyKey)}</p>
        <div className="pos-tour__actions">
          <button type="button" className="pos-tour__btn pos-tour__btn--ghost" onClick={tour.skip}>
            {t('tour.skip')}
          </button>
          <div className="pos-tour__spacer" />
          {!isFirst && (
            <button type="button" className="pos-tour__btn pos-tour__btn--ghost" onClick={tour.prev}>
              {t('tour.prev')}
            </button>
          )}
          <button type="button" className="pos-tour__btn pos-tour__btn--primary" onClick={tour.next}>
            {isLast ? t('tour.finish') : t('tour.next')}
          </button>
        </div>
      </div>
      <style>{styles}</style>
    </div>
  )
}

/** The default ~8-step POS onboarding tour. */
export const POS_TOUR_STEPS: readonly GuidedTourStep[] = [
  { target: '.pos-rail__item[href="/floor"]', bodyKey: 'tour.s.floor' },
  { target: '.pos-rail__item[href="/cashcut"]', bodyKey: 'tour.s.cash' },
  { target: '.pos-rail__item[href="/order"]', bodyKey: 'tour.s.order' },
  { target: '.pos-rail__item[href="/kds"]', bodyKey: 'tour.s.kds' },
  { target: '.pos-rail__item[href="/tickets"]', bodyKey: 'tour.s.tickets' },
  { target: '.pos-rail__item[href="/menu"]', bodyKey: 'tour.s.menu' },
  { target: '.pos-topbar__lock', bodyKey: 'tour.s.lock' },
  { target: '.pos-env-switch', bodyKey: 'tour.s.golive' },
]

const styles = `
  .pos-tour { position: fixed; inset: 0; z-index: var(--pos-z-toast, 300); }
  .pos-tour__scrim { position: fixed; background: var(--pos-overlay, rgba(0,0,0,.55)); }
  .pos-tour__ring {
    position: fixed;
    border-radius: var(--pos-radius-md);
    box-shadow: 0 0 0 2px var(--pos-tint), 0 0 0 6px color-mix(in oklch, var(--pos-tint) 35%, transparent);
    pointer-events: none;
  }
  .pos-tour__card {
    position: fixed;
    box-sizing: border-box;
    padding: var(--pos-space-3) var(--pos-space-4);
    border-radius: var(--pos-radius-lg, var(--pos-radius-md));
    background: var(--pos-surface);
    border: 1px solid var(--pos-separator);
    box-shadow: var(--pos-shadow-4, 0 24px 60px -16px rgba(0,0,0,.55));
    color: var(--pos-fg);
    font-family: var(--font-sans);
  }
  .pos-tour__step {
    font-size: var(--pos-text-xxs);
    font-weight: var(--pos-weight-semibold);
    letter-spacing: var(--pos-tracking-widest);
    text-transform: uppercase;
    color: var(--pos-fg-tertiary);
  }
  .pos-tour__title {
    margin-top: 2px;
    font-family: var(--font-display, var(--font-sans));
    font-size: var(--pos-text-md);
    font-weight: var(--pos-weight-semibold);
  }
  .pos-tour__body {
    margin: var(--pos-space-2) 0 var(--pos-space-3);
    font-size: var(--pos-text-sm);
    line-height: var(--pos-leading-snug, 1.4);
    color: var(--pos-fg-secondary);
  }
  .pos-tour__actions { display: flex; align-items: center; gap: var(--pos-space-2); }
  .pos-tour__spacer { flex: 1; }
  .pos-tour__btn {
    appearance: none;
    border: 1px solid transparent;
    border-radius: var(--pos-radius-md);
    min-height: 36px;
    padding: 0 var(--pos-space-3);
    font: inherit;
    font-size: var(--pos-text-sm);
    font-weight: var(--pos-weight-semibold);
    cursor: pointer;
    transition: background var(--pos-duration-fast) var(--pos-ease-out),
                color var(--pos-duration-fast) var(--pos-ease-out);
  }
  .pos-tour__btn--ghost { background: transparent; color: var(--pos-fg-secondary); }
  .pos-tour__btn--ghost:hover { background: var(--pos-elevated); color: var(--pos-fg); }
  .pos-tour__btn--primary { background: var(--pos-tint); color: var(--pos-tint-on, #fff); }
  .pos-tour__btn--primary:hover { background: var(--pos-tint-strong, var(--pos-tint)); }
  @media (max-width: 520px) {
    .pos-tour__card { padding: var(--pos-space-3); }
  }
`
